import suppliersService from '@/services/suppliers';
import collaboratorsService from '@/services/collaborators';
import financialAccountsService from '@/services/financialAccounts';
import expenseReceiptsService from '@/services/expenseReceipts';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';

export const createDefaultExpenseReceiptsFilterOptions = () => ({
  supplier: [],
  collaborator: [],
  paymentAccount: [],
});

const normalizeRow = (row) => ({
  ...row,
  supplier_id: Number(row?.supplier_id || 0),
  collaborator_id: Number(row?.collaborator_id || 0),
  payment_account_id: Number(row?.payment_account_id || 0),
  total: Number(row?.total || 0),
});

const normalizeCollaboratorOptions = (list = []) => {
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      const id = String(item?.id ?? item?.value ?? '').trim();
      if (id === '') {
        return null;
      }

      const firstName = String(item?.first_name || '').trim();
      const lastName = String(item?.last_name || '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallbackName = String(item?.name || item?.label || '').trim();

      return {
        value: id,
        label: fullName || fallbackName || id,
        meta: item,
      };
    })
    .filter(Boolean);
};

const extractSnapshotMetrics = (response) => {
  const payload = extractPayload(response);
  const metrics = payload && typeof payload === 'object'
    ? payload.metrics
    : null;

  return metrics && typeof metrics === 'object' ? metrics : {};
};

export const buildExpenseReceiptsListParams = ({ toolbarSearch = '', toolbarFilters = {} } = {}) => {
  const filters = toolbarFilters && typeof toolbarFilters === 'object' ? toolbarFilters : {};

  return {
    date_start: String(filters.date_from || '').trim(),
    date_end: String(filters.date_to || '').trim(),
    supplier_id: Array.isArray(filters.supplier_ids) ? filters.supplier_ids : [],
    collaborator_id: Array.isArray(filters.collaborator_ids) ? filters.collaborator_ids : [],
    financial_account_id: Array.isArray(filters.payment_account_ids) ? filters.payment_account_ids : [],
  };
};

export const fetchExpenseReceiptsSnapshot = async (params = {}) => {
  const response = await expenseReceiptsService.index(params);
  const rows = extractCollection(response);

  return {
    rows: (Array.isArray(rows) ? rows : []).map((row) => normalizeRow(row)),
    metrics: extractSnapshotMetrics(response),
  };
};

export const fetchExpenseReceiptsRows = async (params = {}) => {
  const snapshot = await fetchExpenseReceiptsSnapshot(params);
  return Array.isArray(snapshot?.rows) ? snapshot.rows : [];
};

export const fetchExpenseReceiptsFilterOptions = async () => {
  const defaults = createDefaultExpenseReceiptsFilterOptions();

  try {
    const [suppliersResponse, collaboratorsResponse, paymentAccountsResponse] = await Promise.all([
      suppliersService.selectors?.() || Promise.resolve({ data: [] }),
      collaboratorsService.selectors?.() || Promise.resolve({ data: [] }),
      financialAccountsService.selectors?.({}, { skipLoading: true }) || Promise.resolve({ data: [] }),
    ]);

    const supplierOptions = normalizeCatalogOptions(extractCollection(suppliersResponse), { useCodeInLabel: false });
    const collaboratorOptions = normalizeCollaboratorOptions(extractCollection(collaboratorsResponse));
    const paymentAccountOptions = normalizeCatalogOptions(extractCollection(paymentAccountsResponse), { useCodeInLabel: false });

    return {
      ...defaults,
      supplier: supplierOptions,
      collaborator: collaboratorOptions,
      paymentAccount: paymentAccountOptions,
    };
  } catch (error) {
    return defaults;
  }
};
